// Keycloak sign-in: authorization code + PKCE with a public client (docs/auth/keycloak-oauth2-design.md §8.3).
// Tokens live in memory only; a page reload signs in again silently through the Keycloak session cookie.
import { InMemoryWebStorage, WebStorageStateStore } from 'oidc-client-ts';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { AuthProvider as OidcProvider, hasAuthParams, useAuth } from 'react-oidc-context';
import { setAccessToken } from '@/api/client';
import { config } from '@/config';
import { rememberReturnTo } from './returnTo';
import { SignInScreen } from './SignInScreen';

const oidcConfig = {
  authority: config.oidcAuthority,
  client_id: config.oidcClientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  automaticSilentRenew: true,
  // The /auth/callback route (SignInCallback) moves on through the router once sign-in completes; changing
  // the address here behind the router's back would leave it showing the empty callback route.
  onSigninCallback: () => undefined,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  if (config.authDisabled) return <>{children}</>;
  return (
    <OidcProvider {...oidcConfig}>
      <RequireSignIn>{children}</RequireSignIn>
    </OidcProvider>
  );
}

/** Signing in is required: there is no anonymous use of Xpedition2. */
function RequireSignIn({ children }: { children: ReactNode }) {
  const auth = useAuth();

  // After a spell of inactivity the Keycloak session expires, so the background token refresh fails (Keycloak
  // refuses it without CORS headers, so the browser reports "Failed to fetch"). That isn't a sign-in failure:
  // go back through Keycloak - silently if its session is still alive - and return to the same page.
  const sessionExpired = auth.error?.source === 'renewSilent' || auth.error?.source === 'signinSilent';

  useEffect(() => {
    const needsSignIn = !auth.isAuthenticated && !auth.error;
    if (!hasAuthParams() && (needsSignIn || sessionExpired) && !auth.activeNavigator && !auth.isLoading) {
      // "Sign up" (a shared route's page links to ?signup=1): Keycloak's registration page instead of sign-in.
      const params = new URLSearchParams(window.location.search);
      const signUp = params.has('signup');
      // The address to sign up with (an activity invite's: the account must use it to be linked to the invite).
      const hint = params.get('hint') ?? undefined;
      params.delete('signup');
      params.delete('hint');
      const query = params.toString();
      rememberReturnTo(window.location.pathname + (query ? `?${query}` : ''));
      void auth.signinRedirect(
        signUp ? { prompt: 'create', login_hint: hint } : hint ? { login_hint: hint } : undefined,
      );
    }
  }, [auth, sessionExpired]);

  // A token that ran out while the tab was asleep (no refresh was attempted): sign in again the same way.
  useEffect(
    () =>
      auth.events.addAccessTokenExpired(() => {
        rememberReturnTo(window.location.pathname + window.location.search);
        void auth.signinRedirect();
      }),
    [auth],
  );

  // Hand each new or renewed access token to the API client during render, not in an effect: children's
  // effects (their first API calls, e.g. GET /me for the avatar) run before this component's effects would.
  setAccessToken(auth.user?.access_token);

  const retry = () => {
    rememberReturnTo(window.location.pathname + window.location.search);
    void auth.signinRedirect();
  };

  if (auth.error && !sessionExpired) return <SignInScreen error={auth.error.message} onRetry={retry} />;
  if (!auth.isAuthenticated || sessionExpired) return <SignInScreen onRetry={retry} />;
  return <>{children}</>;
}
